import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ACCENT } from "../artistStudio/shared";
import { apiMessage } from "./fanHomeApi";
import { discoveryApi } from "./discoveryApi";
import { useErrorToast } from "../common/errorToast";
import MusicShell from "./MusicShell";
import TrackCard from "./TrackCard";
import { CARD_GRID, SectionFrame } from "./Section";
import { ArtistCard, PlaylistCard, LoadMore, GridSkeleton, Notice } from "./discoveryParts";

// Search and Discovery: one search box over sounds, artists and playlists, with Genre and Mood filters from the
// catalogue. Everything lives in the address (?q=&type=&genre=&mood=) so a search can be shared and the back button works.
const TABS = [["all", "All"], ["sounds", "Sounds"], ["artists", "Artists"], ["playlists", "Playlists"]];
const EMPTY = { sounds: [], artists: [], playlists: [] };

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button" onClick={onClick} aria-pressed={active}
      className={`shrink-0 rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition ${active ? "text-white" : "bg-white text-gray-800 hover:bg-gray-100 border-gray-300"}`}
      style={active ? { backgroundColor: ACCENT, borderColor: ACCENT } : undefined}
    >{children}</button>
  );
}

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") || "").trim().slice(0, 100);
  const type = TABS.some(([k]) => k === params.get("type")) ? params.get("type") : "all";
  const genre = params.get("genre") || "";
  const mood = params.get("mood") || "";
  // Nothing typed or chosen: browse the most played sounds (no tabs). Otherwise search the chosen tab.
  const browse = !q && !genre && !mood;
  const kind = browse ? "sounds" : type;

  const [text, setText] = useState(q);
  const [filters, setFilters] = useState({ genres: [], moods: [] });
  const [state, setState] = useState({ status: "idle", error: "", counts: null, prompt: false });
  useErrorToast(state.status === "error" ? "" : state.error); // a failed "Load more" is a popup; a failed search keeps the Try again panel
  const [lists, setLists] = useState(EMPTY);
  const [more, setMore] = useState({ page: 1, hasMore: false, busy: false });
  const ticket = useRef(0);
  const inputRef = useRef(null);

  // Choosing a tab or a chip is a step the Back button can return to; typing only rewrites the current step.
  const update = useCallback((changes, { replace = false } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setParams(next, { replace });
  }, [params, setParams]);

  // The box follows the address (back button, shared link), and the address follows the box after a short pause.
  useEffect(() => { setText(q); }, [q]);
  useEffect(() => {
    if (text.trim() === q.trim()) return undefined;
    const timer = setTimeout(() => update({ q: text.trim() }, { replace: true }), 350);
    return () => clearTimeout(timer);
  }, [text, q, update]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    let live = true;
    discoveryApi.filters().then((d) => live && setFilters({ genres: d?.genres || [], moods: d?.moods || [] })).catch(() => {});
    return () => { live = false; };
  }, []);

  const load = useCallback(async () => {
    const mine = ++ticket.current;
    setState((s) => ({ ...s, status: "loading", error: "" }));
    try {
      const data = await discoveryApi.search({ q, type: kind, genre: genre || undefined, mood: mood || undefined, page: 1 });
      if (mine !== ticket.current) return;
      setLists({ sounds: data.sounds?.items || [], artists: data.artists?.items || [], playlists: data.playlists?.items || [] });
      const paged = kind === "all" ? null : data[kind];
      setMore({ page: 1, hasMore: Boolean(paged?.has_more), busy: false });
      setState({ status: "ready", error: "", counts: data.counts, prompt: Boolean(data.prompt) });
    } catch (e) {
      if (mine === ticket.current) setState((s) => ({ ...s, status: "error", error: apiMessage(e, "We couldn't search right now.") }));
    }
  }, [q, kind, genre, mood]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setMore((m) => ({ ...m, busy: true }));
    const mine = ticket.current;
    try {
      const page = more.page + 1;
      const data = await discoveryApi.search({ q, type: kind, genre: genre || undefined, mood: mood || undefined, page });
      if (mine !== ticket.current) return;
      setLists((l) => ({ ...l, [kind]: [...l[kind], ...(data[kind]?.items || [])] }));
      setMore({ page, hasMore: Boolean(data[kind]?.has_more), busy: false });
    } catch (e) {
      setMore((m) => ({ ...m, busy: false }));
      setState((s) => ({ ...s, error: apiMessage(e, "We couldn't load more.") }));
    }
  };

  const clearAll = () => { setText(""); setParams(new URLSearchParams()); };
  const genreName = filters.genres.find((g) => g.slug === genre)?.name || genre;
  const moodName = filters.moods.find((m) => m.slug === mood)?.name || mood;
  const counts = state.counts;
  const active = Boolean(q || genre || mood);
  const loading = state.status === "loading";
  const total = counts ? counts.total : 0;
  const shown = kind === "all" ? ["sounds", "artists", "playlists"] : [kind];

  const renderGroup = (kind) => {
    const items = lists[kind];
    if (!items.length) return null;
    const heading = { sounds: "Sounds", artists: "Artists", playlists: "Playlists" }[kind];
    const grid = kind === "artists" ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" : CARD_GRID;
    const cards = (
      <div className={grid}>
        {kind === "sounds" && items.map((t, i, all) => <TrackCard key={t.id} track={t} variant="plain" queue={all} />)}
        {kind === "artists" && items.map((a) => <ArtistCard key={a.id} artist={a} />)}
        {kind === "playlists" && items.map((p) => <PlaylistCard key={p.id} playlist={p} />)}
      </div>
    );
    if (browse) return <div key={kind} className="mt-4">{cards}</div>;
    return (
      <SectionFrame key={kind} id={`search-${kind}`} title={heading} subtitle={type === "all" && counts?.[kind] > items.length ? `Top ${items.length} of ${counts[kind]}` : undefined}>
        {cards}
        {type === "all" && counts?.[kind] > items.length && (
          <button type="button" onClick={() => update({ type: kind })} className="mt-3 text-sm font-semibold hover:underline" style={{ color: ACCENT }}>See all {counts[kind]} {heading.toLowerCase()}</button>
        )}
      </SectionFrame>
    );
  };

  return (
    <MusicShell search={{ text, setText, inputRef, onSubmit: () => update({ q: text.trim() }, { replace: true }) }}>
      <h1 className="text-2xl font-bold text-gray-900">Search</h1>

      <div className="mt-5 space-y-4">
        {filters.genres.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Genre</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Genre">
              <Chip active={!genre} onClick={() => genre && update({ genre: "" })}>All</Chip>
              {filters.genres.map((g) => <Chip key={g.id} active={genre === g.slug} onClick={() => update({ genre: genre === g.slug ? "" : g.slug })}>{g.name}</Chip>)}
            </div>
          </div>
        )}
        {filters.moods.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Mood</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Mood">
              <Chip active={!mood} onClick={() => mood && update({ mood: "" })}>All</Chip>
              {filters.moods.map((m) => <Chip key={m.id} active={mood === m.slug} onClick={() => update({ mood: mood === m.slug ? "" : m.slug })}>{m.name}</Chip>)}
            </div>
          </div>
        )}
      </div>

      {browse && state.status === "ready" && counts && (
        <p className="mt-6 text-sm font-semibold text-gray-900" data-testid="search-summary" aria-live="polite">{total} {total === 1 ? "sound" : "sounds"}</p>
      )}
      {active && (
        <div className="mt-5 flex flex-wrap items-center gap-x-2 text-sm text-gray-600" data-testid="search-summary" aria-live="polite">
          {state.status === "ready" && <span className="font-semibold text-gray-900">{total} {total === 1 ? "result" : "results"}</span>}
          {q && <span>· “{q}”</span>}
          {genreName && <span>· {genreName}</span>}
          {moodName && <span>· {moodName}</span>}
          <button type="button" onClick={clearAll} className="font-semibold hover:underline" style={{ color: ACCENT }}>Clear</button>
        </div>
      )}

      {!browse && <div className="mt-4 flex gap-2 border-b border-gray-200" role="tablist" aria-label="Result type">
        {TABS.map(([key, label]) => (
          <button
            key={key} type="button" role="tab" aria-selected={type === key} onClick={() => update({ type: key === "all" ? "" : key })}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${type === key ? "text-gray-900" : "border-transparent text-gray-500 hover:text-gray-800"}`}
            style={type === key ? { borderColor: ACCENT } : undefined}
          >
            {label}{counts && key !== "all" ? <span className="ml-1 text-xs text-gray-400">{counts[key]}</span> : null}
          </button>
        ))}
      </div>}

      <div className="mt-2" data-testid="search-results">
        {state.status === "error" && !total ? (
          <Notice action={<button type="button" onClick={load} className="mt-3 rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>Try again</button>}>{state.error}</Notice>
        ) : (loading || !counts) && !total ? (
          <div className="mt-8"><GridSkeleton /></div>
        ) : state.status === "ready" && counts && browse && total === 0 ? (
          <div className="mt-8"><Notice>No sounds are available yet. Check back soon.</Notice></div>
        ) : state.status === "ready" && counts && (type === "all" ? total === 0 : counts[type] === 0) ? (
          <div className="mt-8">
            <Notice action={<button type="button" onClick={clearAll} className="mt-3 rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>Clear search</button>}>
              No results{q ? ` for “${q}”` : ""}{genreName || moodName ? ` in ${[genreName, moodName].filter(Boolean).join(" · ")}` : ""}. Check the spelling, try a shorter word, or remove a filter.
            </Notice>
          </div>
        ) : (
          <>
            {shown.map(renderGroup)}
            {kind !== "all" && more.hasMore && <LoadMore onClick={loadMore} busy={more.busy} />}
          </>
        )}
      </div>
    </MusicShell>
  );
}
